import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "../api/axios";
import { 
  HiOutlineRefresh, 
  HiOutlineSave,
  HiOutlineShieldCheck,
  HiPlus,
  HiUpload,
  HiX
} from "react-icons/hi";

const RateLimitSettings = () => {
  const { getToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newIpInput, setNewIpInput] = useState("");

  const [config, setConfig] = useState({
    protectionStatus: "ACTIVE",
    trackedIps: 0,
    blockedIps: 0,
    totalRequests: 0,
    
    coreTimeWindow: "15",
    coreBlockDuration: "60",
    coreMaxRequests: "100",
    
    trialLimit: "25",
    developerLimit: "50",
    enterpriseLimit: "500",
    educationalLimit: "25",
    
    whitelistedIps: "",
    
    suspiciousPatterns: 0,
    analysisResults: 0,
    
    dailyDate: new Date().toLocaleDateString(),
    dailyTriggers: 0,
    dailyUniqueIps: 0,
    dailyUniqueEmails: 0
  });

  const fetchConfig = async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      const response = await axios.get('/admin/rate-limit/config', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log("Backend endpoint not ready, falling back to local storage");
      const savedConfig = localStorage.getItem("rateLimitConfig");
      if (savedConfig) {
        setConfig(prev => ({ ...prev, ...JSON.parse(savedConfig) }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = await getToken();
      await axios.post('/admin/rate-limit/config', config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      localStorage.setItem("rateLimitConfig", JSON.stringify(config));
    } catch (error) {
      console.log("Backend endpoint not ready, saving to local storage");
      localStorage.setItem("rateLimitConfig", JSON.stringify(config));
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleAddIp = () => {
    if (!newIpInput.trim()) return;
    const currentIps = config.whitelistedIps ? config.whitelistedIps.split('\n').filter(ip => ip.trim()) : [];
    if (!currentIps.includes(newIpInput.trim())) {
      const updatedIps = [...currentIps, newIpInput.trim()].join('\n');
      handleChange("whitelistedIps", updatedIps);
    }
    setNewIpInput("");
  };

  const handleRemoveIp = (ipToRemove) => {
    const currentIps = config.whitelistedIps ? config.whitelistedIps.split('\n').filter(ip => ip.trim()) : [];
    const updatedIps = currentIps.filter(ip => ip !== ipToRemove).join('\n');
    handleChange("whitelistedIps", updatedIps);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const newIps = text.split(/\r?\n/).filter(ip => ip.trim());
      const currentIps = config.whitelistedIps ? config.whitelistedIps.split('\n').filter(ip => ip.trim()) : [];
      
      const combinedIps = Array.from(new Set([...currentIps, ...newIps])).join('\n');
      handleChange("whitelistedIps", combinedIps);
    };
    reader.readAsText(file);
    e.target.value = null; // Reset file input
  };

  const StatCard = ({ title, value, status }) => (
    <div className="bg-[#EDF4E3] border border-[#D5E6BE] rounded-lg p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <HiOutlineShieldCheck className="w-5 h-5 text-[#86B93F]" />
          <span className="text-[#161616] text-xs font-bold">{title}</span>
        </div>
        {status && (
          <span className="text-[10px] font-bold text-[#86B93F] border border-[#86B93F]/30 bg-[#86B93F]/10 px-2 py-0.5 rounded-full uppercase tracking-wide">
            {status}
          </span>
        )}
      </div>
      <div className="text-2xl font-normal text-[#161616]">{value}</div>
    </div>
  );

  return (
    <div className="pb-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-white">Rate Limiting Configuration</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchConfig}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-white text-[#161616] rounded-full text-xs font-bold hover:bg-gray-100 transition-colors"
          >
            <HiOutlineRefresh className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Reload
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-white text-[#161616] rounded-full text-xs font-bold hover:bg-gray-100 transition-colors"
          >
            <HiOutlineSave className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-[#E6F0D8] rounded-xl p-6 sm:p-8 space-y-6 min-h-[800px]">
        
        {/* Top Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Protection Status" value={config.protectionStatus === "ACTIVE" ? "O" : "-"} status={config.protectionStatus} />
          <StatCard title="Tracked IPs" value={config.trackedIps} status="ACTIVE" />
          <StatCard title="Blocked IPs" value={config.blockedIps} status="ACTIVE" />
          <StatCard title="Total Requests" value={config.totalRequests} status="ACTIVE" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Core Settings */}
          <div className="bg-[#EDF4E3] border border-[#D5E6BE] rounded-lg p-6 shadow-sm">
            <div className="mb-6">
              <h2 className="text-[#161616] font-bold text-lg">Core Settings</h2>
              <p className="text-[#161616]/60 text-xs mt-1">Configure rate limiting behavior</p>
            </div>
            <div className="space-y-4">
              <div className="relative">
                <label className="block text-[#161616] text-xs font-bold mb-1.5">Time Window *</label>
                <div className="relative flex items-center">
                  <input
                    type="number"
                    value={config.coreTimeWindow}
                    onChange={(e) => handleChange("coreTimeWindow", e.target.value)}
                    className="w-full bg-white border-0 rounded-lg px-4 py-2.5 text-[#161616] text-sm shadow-sm focus:ring-2 focus:ring-[#BDE958] outline-none"
                  />
                  <span className="absolute right-4 text-red-400 text-xs font-medium">min</span>
                </div>
              </div>
              <div className="relative">
                <label className="block text-[#161616] text-xs font-bold mb-1.5">Block Duration *</label>
                <div className="relative flex items-center">
                  <input
                    type="number"
                    value={config.coreBlockDuration}
                    onChange={(e) => handleChange("coreBlockDuration", e.target.value)}
                    className="w-full bg-white border-0 rounded-lg px-4 py-2.5 text-[#161616] text-sm shadow-sm focus:ring-2 focus:ring-[#BDE958] outline-none"
                  />
                  <span className="absolute right-4 text-red-400 text-xs font-medium">min</span>
                </div>
              </div>
              <div className="relative">
                <label className="block text-[#161616] text-xs font-bold mb-1.5">Max Requests *</label>
                <div className="relative flex items-center">
                  <input
                    type="number"
                    value={config.coreMaxRequests}
                    onChange={(e) => handleChange("coreMaxRequests", e.target.value)}
                    className="w-full bg-white border-0 rounded-lg px-4 py-2.5 text-[#161616] text-sm shadow-sm focus:ring-2 focus:ring-[#BDE958] outline-none"
                  />
                  <span className="absolute right-4 text-red-400 text-xs font-medium">req</span>
                </div>
              </div>
            </div>
          </div>

          {/* Plan Rate Limits */}
          <div className="bg-[#EDF4E3] border border-[#D5E6BE] rounded-lg p-6 shadow-sm">
            <div className="mb-6">
              <h2 className="text-[#161616] font-bold text-lg">Plan Rate Limits</h2>
              <p className="text-[#161616]/60 text-xs mt-1">Set request limits per plan tier</p>
            </div>
            <div className="space-y-3">
              {[
                { id: 'trialLimit', title: 'Trial', desc: 'Basic access for new users' },
                { id: 'developerLimit', title: 'Developer', desc: 'Standard developer access' },
                { id: 'enterpriseLimit', title: 'Enterprise', desc: 'High volume enterprise access' },
                { id: 'educationalLimit', title: 'Educational', desc: 'Access for students and academics' }
              ].map((plan) => (
                <div key={plan.id} className="bg-white rounded-lg p-3 flex items-center justify-between shadow-sm">
                  <div>
                    <h3 className="text-[#161616] text-sm font-bold">{plan.title}</h3>
                    <p className="text-[#161616]/50 text-[10px]">{plan.desc}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={config[plan.id]}
                      onChange={(e) => handleChange(plan.id, e.target.value)}
                      className="w-16 bg-[#BDE958]/30 border-0 rounded px-2 py-1.5 text-center text-[#161616] text-sm font-medium focus:ring-2 focus:ring-[#BDE958] outline-none"
                    />
                    <span className="text-[#161616]/60 text-[10px] font-bold">req/min</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* IP Whitelist */}
        <div className="bg-[#EDF4E3] border border-[#D5E6BE] rounded-lg p-6 shadow-sm">
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h2 className="text-[#161616] font-bold text-lg">IP Whitelist</h2>
              <p className="text-[#161616]/60 text-xs mt-1">IPs that bypass rate limiting</p>
            </div>
            <div className="relative overflow-hidden inline-block">
              <button className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[#D5E6BE] text-[#161616] rounded-lg text-xs font-bold hover:bg-[#F3F7EC] transition-colors cursor-pointer">
                <HiUpload className="w-4 h-4 text-[#86B93F]" />
                Upload CSV/TXT
              </button>
              <input 
                type="file" 
                accept=".txt,.csv" 
                onChange={handleFileUpload}
                className="absolute left-0 top-0 opacity-0 w-full h-full cursor-pointer"
              />
            </div>
          </div>
          <div className="bg-[#E5EFD5] rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <label className="text-[#161616] text-xs font-bold">Whitelisted IPs</label>
              <span className="text-[10px] font-bold text-[#161616]/60 bg-white/50 px-2 py-0.5 rounded-full">
                {config.whitelistedIps ? config.whitelistedIps.split('\n').filter(ip => ip.trim()).length : 0} IPs whitelisted
              </span>
            </div>
            
            <div className="flex items-center gap-2 mb-4">
              <input
                type="text"
                value={newIpInput}
                onChange={(e) => setNewIpInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddIp()}
                placeholder="e.g., 192.168.1.1 or 10.0.0.0/24"
                className="flex-1 bg-white border-0 rounded-lg px-4 py-2 text-[#161616] text-sm shadow-sm focus:ring-2 focus:ring-[#BDE958] outline-none font-mono"
              />
              <button
                onClick={handleAddIp}
                disabled={!newIpInput.trim()}
                className="flex items-center justify-center px-4 py-2 bg-[#BDE958] text-[#161616] rounded-lg text-sm font-bold shadow-sm hover:bg-[#a8d44a] disabled:opacity-50 transition-colors"
              >
                <HiPlus className="w-4 h-4 mr-1" /> Add
              </button>
            </div>
            
            <div className="bg-white/60 border border-white/40 rounded-lg p-3 min-h-[100px] max-h-[200px] overflow-y-auto">
              {!config.whitelistedIps || config.whitelistedIps.trim() === "" ? (
                <div className="flex h-full items-center justify-center text-[#161616]/40 text-xs italic py-4">
                  No IPs whitelisted yet
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {config.whitelistedIps.split('\n').filter(ip => ip.trim()).map((ip, index) => (
                    <div key={index} className="flex items-center gap-1.5 bg-white border border-[#D5E6BE] px-2.5 py-1 rounded-md shadow-sm group">
                      <span className="text-[#161616] text-xs font-mono">{ip}</span>
                      <button 
                        onClick={() => handleRemoveIp(ip)}
                        className="text-[#161616]/40 hover:text-red-500 hover:bg-red-50 rounded-full p-0.5 transition-colors"
                      >
                        <HiX className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-[#161616]/50 text-[10px] mt-3">Whitelisted IPs are exempt from all rate limiting checks.</p>
          </div>
        </div>

        {/* Active Blocked IPs */}
        <div className="bg-[#EDF4E3] border border-[#D5E6BE] rounded-lg shadow-sm overflow-hidden">
          <div className="p-6 border-b border-[#D5E6BE]">
            <h2 className="text-[#161616] font-bold text-lg">Active Blocked IPs</h2>
            <p className="text-[#161616]/60 text-xs mt-1">Currently blocked IP addresses and their details</p>
          </div>
          <div className="p-12 flex items-center justify-center bg-[#E6F0D8]">
            <span className="text-[#161616]/40 text-xs font-medium">No IPs are currently blocked</span>
          </div>
        </div>

        {/* Email Patterns Analysis */}
        <div className="bg-[#EDF4E3] border border-[#D5E6BE] rounded-lg shadow-sm overflow-hidden">
          <div className="p-6 border-b border-[#D5E6BE]">
            <h2 className="text-[#161616] font-bold text-lg">Email Patterns Analysis</h2>
            <p className="text-[#161616]/60 text-xs mt-1">Suspicious email patterns and analysis results</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#D5E6BE]">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[#161616] font-bold text-sm">Suspicious Patterns</h3>
                <span className="text-[10px] font-bold text-[#D97706] bg-[#FEF3C7] px-2 py-0.5 rounded-full">
                  {config.suspiciousPatterns} patterns
                </span>
              </div>
              <p className="text-[#161616]/50 text-xs">No suspicious patterns detected</p>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[#161616] font-bold text-sm">Analysis Results</h3>
                <span className="text-[10px] font-bold text-[#2563EB] bg-[#DBEAFE] px-2 py-0.5 rounded-full">
                  {config.analysisResults} patterns
                </span>
              </div>
              <p className="text-[#161616]/50 text-xs">No analysis results available</p>
            </div>
          </div>
        </div>

        {/* Daily Statistics */}
        <div className="bg-[#EDF4E3] border border-[#D5E6BE] rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h2 className="text-[#161616] font-bold text-lg">Daily Statistics</h2>
            <p className="text-[#161616]/60 text-xs mt-1">Today's rate limiting activity and triggers</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-[#161616]/60 text-xs font-bold mb-2">Date</div>
              <div className="text-[#161616] text-sm font-bold">{config.dailyDate}</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-[#161616]/60 text-xs font-bold mb-2">Total Triggers</div>
              <div className="text-[#161616] text-sm font-bold">{config.dailyTriggers}</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-[#161616]/60 text-xs font-bold mb-2">Unique IPs</div>
              <div className="text-[#161616] text-sm font-bold">{config.dailyUniqueIps}</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-[#161616]/60 text-xs font-bold mb-2">Unique Emails</div>
              <div className="text-[#161616] text-sm font-bold">{config.dailyUniqueEmails}</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default RateLimitSettings;
